import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { useEffect } from 'react';
import { emitLiveUpdateStatus } from './liveUpdateStatusBus';
import { Capacitor } from '@capacitor/core';
import { getDomain } from '../utils/domain';
import Api from '../ajax';
import { formatAppVersion, getNativeAppInfo } from './appVersion';

const reportDeviceVersion = async (otaVersion) => {
  const playstoreVersion = formatAppVersion(await getNativeAppInfo());

  if (!otaVersion && !playstoreVersion) return;
  new Api().post(
    '/report-device-version',
    { ota_version: otaVersion, playstore_version: playstoreVersion },
    (res) => console.warn('[LiveUpdate] report-device-version response', res),
    (err) => console.error('[LiveUpdate] report-device-version failed', err),
  );
};

function compareSemver(a, b) {
  // Returns true if b > a (minor or patch)
  const [majA, minA, patA] = a.split('.').map(Number);
  const [majB, minB, patB] = b.split('.').map(Number);

  if (majB > majA) return false; // Major update, block live update
  if (minB > minA) return true; // Minor update
  if (minB === minA && patB > patA) return true; // Patch/hotfix

  return false;
}

const STARTUP_GRACE_PERIOD_MS = 8000;

export const useLiveUpdate = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let bundleListener;
    let visibilityHandler;
    let pendingBundleId = null;

    const applyPendingReload = async () => {
      if (!pendingBundleId) return;
      const bundleId = pendingBundleId;

      pendingBundleId = null;
      emitLiveUpdateStatus({ status: 'reloading', message: 'Reloading app to new version...' });
      await LiveUpdate.reload();
      localStorage.setItem('bundleId', bundleId);
    };

    const initializeApp = async () => {
      if (cancelled) return;

      emitLiveUpdateStatus({ status: 'checking', message: 'Preparing live update...' });

      const readyResult = await LiveUpdate.ready();

      console.warn('[LiveUpdate] ready() result', readyResult);
      reportDeviceVersion(readyResult?.currentBundleId);

      // Register nextBundleSet listener ONCE, before any download

      if (!bundleListener) {
        bundleListener = await LiveUpdate.addListener(
          'nextBundleSet',
          async (event) => {
            pendingBundleId = event.bundleId;

            if (document.visibilityState === 'hidden') {
              await applyPendingReload();
            } else {
              emitLiveUpdateStatus({ status: 'updating', message: 'Update downloaded — will apply next time the app is idle.' });
            }
          },
        );
      }

      if (!visibilityHandler) {
        visibilityHandler = () => {
          if (document.visibilityState === 'hidden') applyPendingReload();
        };
        document.addEventListener('visibilitychange', visibilityHandler);
      }


      const localBundleId = localStorage.getItem('bundleId') || '0.3.0';

      try {
        emitLiveUpdateStatus({ status: 'checking', message: 'Checking for update...' });
        const subDomain = getDomain().split('.')[0];
        const currentTime = new Date().getTime();
        const res = await fetch(`https://bcdn.classpep.in/pwabuild/${subDomain}/latest.json?time=${currentTime}`);


        if (!res.ok) {
          emitLiveUpdateStatus({ status: 'error', message: `[LiveUpdate] HTTP error! status: ${res.status}` });
          throw new Error(`[LiveUpdate] HTTP error! status: ${res.status}`);
        }
        const manifest = await res.json();


        if (!manifest?.bundleId || !manifest?.downloadUrl) {
          emitLiveUpdateStatus({ status: 'error', message: 'Manifest missing bundleId or downloadUrl' });
          console.warn('⚠️ [LiveUpdate] Manifest missing bundleId or downloadUrl');
          
          return;
        }

        // Check if patch difference is greater than 3
        const [majA, minA, patA] = (localBundleId || '0.3.0').split('.').map(Number);
        const [majB, minB, patB] = manifest.bundleId.split('.').map(Number);
        const patchDiff = (majA === majB && minA === minB) ? (patB - patA) : 0;

        // Block live update between 8:00-9:30am and 3:30-5:00pm, unless patchDiff > 3
        if (patchDiff <= 3) {
          const now = new Date();
          const minutes = now.getHours() * 60 + now.getMinutes();
          // 8:00-9:30am = 480-570, 3:30-5:00pm = 930-1020
          const inMorningBlock = minutes >= 480 && minutes < 570;
          const inEveningBlock = minutes >= 930 && minutes < 1020;

          if (inMorningBlock || inEveningBlock) {
            emitLiveUpdateStatus({ status: 'skipped', message: 'Live update skipped due to restricted time window.' });
            console.warn('[LiveUpdate] Skipping live update due to restricted time window.');
            
            return;
          }
        }
        // Ensure the downloadUrl is always https
        let downloadUrl = `${manifest.downloadUrl}?time=${currentTime}`;

        if (downloadUrl && downloadUrl.startsWith('http://')) {
          downloadUrl = downloadUrl.replace('http://', 'https://');
        }

        if (compareSemver(localBundleId, manifest.bundleId)) {
          try {
            emitLiveUpdateStatus({ status: 'downloading', message: `Downloading update (v${manifest.bundleId})...` });
            await LiveUpdate.downloadBundle({
              url: downloadUrl,
              bundleId: manifest.bundleId,
            });
            emitLiveUpdateStatus({ status: 'updating', message: `Updating to version v${manifest.bundleId}...` });
            await LiveUpdate.setNextBundle({ bundleId: manifest.bundleId });
            emitLiveUpdateStatus({ status: 'done', message: `App updated to v${manifest.bundleId}` });
          } catch (err) {
            emitLiveUpdateStatus({ status: 'error', message: `[LiveUpdate] Error downloading or setting next bundle: ${err.message}` });
            console.error('[LiveUpdate] Error downloading or setting next bundle', err);
          }
        } else {
          emitLiveUpdateStatus({ status: 'done', message: `App is up to date (v${manifest.bundleId})` });
          console.warn('✅ [LiveUpdate] App is up to date or major update required. No update needed.');
        }
      } catch (error) {
        emitLiveUpdateStatus({ status: 'error', message: `[LiveUpdate] Error fetching latest.json or processing update: ${error.message}` });
        console.error('[LiveUpdate] Error fetching latest.json or processing update:', error);
      }
    };

    const startTimer = setTimeout(initializeApp, STARTUP_GRACE_PERIOD_MS);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (bundleListener) bundleListener.remove();
      if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, []);
};
